import React from 'react';
import moment from 'moment';
import { TransfersLoading } from '../Transfers.loading';
import { TransfersEmpty } from '../Transfers.empty';
import { TransfersError } from '../Transfers.error';
import {
  useScheduledTransfersQuery,
  ScheduledTransferDTO,
} from '../../../../../../../../../api/useScheduledTransfersQuery';
import { DepositFrequencyType } from '../../../../../../../../../../ApexOPL/TransferMoney/api/useAchFundingOptionsQuery';
import { formatCurrency } from '../../../../../../../../../../CommonUtils/formatNumber';
import { IconButton } from '../../../../../../../../../../CommonComponents/OPL/IconButton/IconButton';
import { Text } from '../../../../../../../../../../CommonComponents/OPL/Text/Text';
import { Tooltip } from '../../../../../../../../../../CommonComponents/OPL/Tooltip/Tooltip';
import {
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
} from '../../../../../../../../../../CommonComponents/OPL/Table/Table';

const parseFrequency = transfer => {
  const { fundingFrequency, firstDepositDate, secondDepositDate } = transfer;

  const formattedDate = date => moment(date).format('Do');

  switch (fundingFrequency) {
    case DepositFrequencyType.Weekly:
      return `${formattedDate(firstDepositDate)} of every week`;
    case DepositFrequencyType.EveryOtherWeek:
      return `${formattedDate(firstDepositDate)} of every other week`;
    case DepositFrequencyType.TwiceAMonth:
      return `Every ${formattedDate(firstDepositDate)} and
          ${formattedDate(secondDepositDate)} of the month`;
    case DepositFrequencyType.Monthly:
      return `${formattedDate(firstDepositDate)} of every month`;
    case DepositFrequencyType.Quarterly:
      return `${formattedDate(firstDepositDate)} of every quarter`;
    default:
      return null;
  }
};

interface ScheduledTransfersProps {
  managedAccountId: string;
  setSelectedTransfer(transfer: ScheduledTransferDTO): void;
  setIsConfirmationModalOpen(isOpen: boolean): void;
}

export const ScheduledTransfers: React.FC<ScheduledTransfersProps> = ({
  managedAccountId,
  setSelectedTransfer,
  setIsConfirmationModalOpen,
}) => {
  const { data, isError, isFetching, refetch } = useScheduledTransfersQuery(managedAccountId);

  if (isFetching) {
    return <TransfersLoading />;
  }

  if (isError) {
    return <TransfersError onRetry={refetch} />;
  }

  if (!data) {
    return null;
  }

  if (data.length === 0) {
    return <TransfersEmpty />;
  }

  return (
    <>
      <TableHeader>
        <TableRow>
          <TableCell width="11%">
            <Text>Date</Text>
          </TableCell>
          <TableCell width="33%">
            <Text>Name</Text>
          </TableCell>
          <TableCell width="20%">
            <Text>Frequency</Text>
          </TableCell>
          <TableCell width="29%" justifyContent="end">
            <Text>Value</Text>
          </TableCell>
          <TableCell width="7%" justifyContent="center">
            &nbsp;
          </TableCell>
        </TableRow>
      </TableHeader>

      <TableBody maxHeight="264">
        {data.map(transfer => (
          <TableRow key={transfer.scheduledTransferId} height={36}>
            <TableCell width="11%">
              <Text>{moment(transfer.firstDepositDate).format('MM/DD/YYYY')}</Text>
            </TableCell>
            <TableCell width="33%" display="flex" flexDirection="column" alignItems="flex-start">
              <Text fontWeight={500}>{transfer.accountName}</Text>
              <Text color="neutral.600" fontSize="small">
                {transfer.institutionName}
              </Text>
            </TableCell>
            <TableCell width="20%" display="flex" flexDirection="column" alignItems="flex-start">
              <Text fontWeight={500}>{transfer.fundingFrequencyDescription}</Text>
              <Text color="neutral.600" fontSize="small">
                {parseFrequency(transfer)}
              </Text>
            </TableCell>
            <TableCell width="29%" justifyContent="end">
              <Text>{formatCurrency(transfer.amount)}</Text>
            </TableCell>
            <TableCell width="7%" justifyContent="center">
              <Tooltip content="Cancel Scheduled Transfer" placement="top-start">
                <IconButton
                  variant="subtleInactive"
                  icon="fal fa-times"
                  onClick={() => {
                    setSelectedTransfer(transfer);
                    setIsConfirmationModalOpen(true);
                  }}
                />
              </Tooltip>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </>
  );
};
